import { JobCard, JobCardConfig, VehicleCheckedItems } from '@/app/types/jobCard';

// Helper: resolve status code to display name
function getStatusName(code: string, config: JobCardConfig): string {
  const status = config.statuses.find((s) => s.code === code);
  return status?.name || code;
}

// Helper: resolve priority code to display name
function getPriorityName(code: string, config: JobCardConfig): string {
  const priority = config.priorities.find((p) => p.code === code);
  return priority?.name || code;
}

// Helper: format number as currency (INR)
function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0.00';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

// Helper: format ISO date string to readable format
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// Helper: get completion status display name
function getCompletionStatusName(code: string, config: JobCardConfig): string {
  const status = config.item_completion_statuses?.find((s) => s.code === code);
  return status?.name || code;
}

// Helper: get job card type display name
function getJobCardTypeName(code: string | null, config: JobCardConfig): string {
  if (!code) return '-';
  const type = config.job_card_types?.find((t) => t.code === code);
  return type?.name || code;
}

// Helper: get priority color for badge
function getPriorityColor(code: string, config: JobCardConfig): string {
  const priority = config.priorities.find((p) => p.code === code);
  if (priority?.color) return priority.color;
  switch (code) {
    case 'urgent':
      return '#e74c3c';
    case 'high':
      return '#e67e22';
    case 'normal':
      return '#3498db';
    case 'low':
      return '#95a5a6';
    default:
      return '#333';
  }
}

// Build vehicle condition checklist HTML section
function buildChecklistHTML(
  checkedItems: VehicleCheckedItems | null,
  config: JobCardConfig,
): string {
  if (!checkedItems) return '';

  // Check if there's at least one non-empty value
  const hasAnyValue = Object.entries(checkedItems).some(([, value]) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    return true;
  });

  if (!hasAnyValue) return '';

  const items = config.checklist_items
    .map((ci) => {
      const value = (checkedItems as Record<string, unknown>)[ci.key];
      if (ci.type === 'boolean') {
        const checked = value === true;
        return `<div class="checklist-item">
          <span class="check-icon">${checked ? '\u2611' : '\u2610'}</span>
          <span class="check-label">${ci.label}</span>
        </div>`;
      } else {
        // text type
        const textValue = typeof value === 'string' && value.trim() ? value : '-';
        return `<div class="checklist-item">
          <span class="check-label">${ci.label}:</span>
          <span class="check-value">${textValue}</span>
        </div>`;
      }
    })
    .join('');

  return `<div class="checklist-box">
    <h3>Vehicle Condition Checklist</h3>
    <div class="checklist-grid">
      ${items}
    </div>
  </div>`;
}

export function printJobCard(jobCard: JobCard, config: JobCardConfig, orgName: string): void {
  const vehicle = jobCard.vehicle;
  const party = jobCard.party;
  const leadTechnician = jobCard.lead_technician;

  // Build vehicle info
  const makeModel = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || '-';
  const fuelType = vehicle?.fuel_type
    ? vehicle.fuel_type.charAt(0).toUpperCase() + vehicle.fuel_type.slice(1)
    : '-';

  // Build items table rows
  const itemsHTML = jobCard.items
    .map((item, index) => {
      const amount = parseFloat(item.amount || '0');
      const rate = parseFloat(item.rate || '0');
      const qty = parseFloat(item.quantity || '0');
      const techName = item.technician?.name || '-';
      const completionStatus = getCompletionStatusName(item.completion_status, config);

      return `
      <tr>
        <td>${index + 1}</td>
        <td>${item.item?.name || '-'}</td>
        <td class="amount">${qty % 1 === 0 ? qty.toFixed(0) : qty.toFixed(2)}</td>
        <td class="amount">${formatCurrency(rate)}</td>
        <td class="amount">${formatCurrency(amount)}</td>
        <td>${techName}</td>
        <td>${item.notes || '-'}</td>
        <td>${completionStatus}</td>
      </tr>`;
    })
    .join('');

  // Calculate total
  const totalAmount = parseFloat(jobCard.total_amount || '0');

  const priorityColor = getPriorityColor(jobCard.priority, config);

  // Build vehicle condition checklist HTML
  const checklistHTML = buildChecklistHTML(jobCard.vehicle_checked_items, config);

  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Job Card - ${jobCard.document_number}</title>
  <style>
    /* Reset and base styles */
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #333; padding: 20px; }

    /* Header */
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
    .header h1 { font-size: 14px; text-transform: uppercase; letter-spacing: 1px; color: #666; }
    .header h2 { font-size: 20px; font-weight: bold; margin: 5px 0; }
    .doc-info { display: flex; justify-content: space-between; margin-top: 8px; font-size: 12px; }
    .doc-info .left { text-align: left; }
    .doc-info .right { text-align: right; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; color: #fff; }
    .priority-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; color: #fff; }

    /* Info sections */
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px; }
    .info-box { border: 1px solid #ddd; border-radius: 4px; padding: 10px; }
    .info-box h3 { font-size: 11px; text-transform: uppercase; color: #666; margin-bottom: 6px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
    .info-row { display: flex; margin-bottom: 3px; }
    .info-label { font-weight: bold; width: 140px; flex-shrink: 0; }
    .info-value { flex: 1; }

    /* Complaint box */
    .complaint-box { border: 2px solid #e74c3c; border-radius: 4px; padding: 10px; margin-bottom: 15px; background: #fdf2f2; }
    .complaint-box h3 { color: #e74c3c; font-size: 12px; text-transform: uppercase; margin-bottom: 6px; }

    /* Items table */
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
    .items-table th { background: #f5f5f5; border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 11px; text-transform: uppercase; }
    .items-table td { border: 1px solid #ddd; padding: 6px 8px; }
    .items-table .amount { text-align: right; }
    .items-table tfoot td { font-weight: bold; background: #f9f9f9; }

    /* Observations */
    .observations { border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 15px; }
    .observations h3 { font-size: 11px; text-transform: uppercase; color: #666; margin-bottom: 6px; }

    /* Notes */
    .notes-section { border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 15px; }
    .notes-section h3 { font-size: 11px; text-transform: uppercase; color: #666; margin-bottom: 6px; }

    /* Vehicle Checklist */
    .checklist-box { border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 15px; page-break-inside: avoid; }
    .checklist-box h3 { font-size: 11px; text-transform: uppercase; color: #666; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
    .checklist-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 15px; }
    .checklist-item { display: flex; align-items: baseline; margin-bottom: 2px; }
    .checklist-item .check-icon { width: 16px; flex-shrink: 0; font-size: 13px; }
    .checklist-item .check-label { font-weight: bold; width: 130px; flex-shrink: 0; font-size: 11px; }
    .checklist-item .check-value { flex: 1; font-size: 11px; }

    /* Footer */
    .footer { margin-top: 30px; display: flex; justify-content: space-between; }
    .signature-line { width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px; font-size: 11px; }

    /* Page break control */
    .info-grid, .complaint-box, .checklist-box, .observations, .notes-section, .footer { page-break-inside: avoid; }
    .items-table tr { page-break-inside: avoid; }

    @media print {
      body { padding: 10px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <h1>${orgName}</h1>
    <h2>JOB CARD</h2>
    <div class="doc-info">
      <div class="left">
        <strong>${jobCard.document_number}</strong>
        &nbsp;&nbsp;|&nbsp;&nbsp;
        Date: ${formatDate(jobCard.transaction_date)}
      </div>
      <div class="right">
        <span class="badge" style="background: #555;">${getStatusName(jobCard.status, config)}</span>
        &nbsp;
        <span class="priority-badge" style="background: ${priorityColor};">${getPriorityName(jobCard.priority, config)}</span>
        ${jobCard.job_card_type ? `&nbsp;&nbsp;|&nbsp;&nbsp;Type: ${getJobCardTypeName(jobCard.job_card_type, config)}` : ''}
      </div>
    </div>
  </div>

  <!-- Info Grid -->
  <div class="info-grid">
    <!-- Vehicle Info -->
    <div class="info-box">
      <h3>Vehicle Information</h3>
      <div class="info-row">
        <span class="info-label">Registration:</span>
        <span class="info-value">${vehicle?.registration_number || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Make / Model:</span>
        <span class="info-value">${makeModel}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Fuel Type:</span>
        <span class="info-value">${fuelType}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Year:</span>
        <span class="info-value">${vehicle?.year_of_manufacture || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Odometer:</span>
        <span class="info-value">${jobCard.odometer_reading != null ? jobCard.odometer_reading.toLocaleString('en-IN') + ' km' : '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Fuel Level:</span>
        <span class="info-value">${jobCard.fuel_level || '-'}</span>
      </div>
    </div>

    <!-- Customer & Job Info -->
    <div class="info-box">
      <h3>Customer & Job Details</h3>
      <div class="info-row">
        <span class="info-label">Customer:</span>
        <span class="info-value">${party?.name || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Phone:</span>
        <span class="info-value">${party?.phone || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Bay:</span>
        <span class="info-value">${jobCard.bay_number || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Lead Technician:</span>
        <span class="info-value">${leadTechnician?.name || '-'}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Est. Completion:</span>
        <span class="info-value">${formatDate(jobCard.estimated_completion_date)}</span>
      </div>
      ${jobCard.actual_completion_date ? `
      <div class="info-row">
        <span class="info-label">Completed:</span>
        <span class="info-value">${formatDate(jobCard.actual_completion_date)}</span>
      </div>` : ''}
      ${jobCard.reference_number ? `
      <div class="info-row">
        <span class="info-label">Reference:</span>
        <span class="info-value">${jobCard.reference_number}</span>
      </div>` : ''}
    </div>
  </div>

  <!-- Customer Complaint -->
  ${jobCard.customer_complaint ? `
  <div class="complaint-box">
    <h3>Customer Complaint</h3>
    <p>${jobCard.customer_complaint}</p>
  </div>` : ''}

  <!-- Vehicle Condition Checklist -->
  ${checklistHTML}

  <!-- Items Table -->
  ${jobCard.items && jobCard.items.length > 0 ? `
  <table class="items-table">
    <thead>
      <tr>
        <th>#</th>
        <th>Item</th>
        <th style="text-align: right;">Qty</th>
        <th style="text-align: right;">Rate</th>
        <th style="text-align: right;">Amount</th>
        <th>Technician</th>
        <th>Notes</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      ${itemsHTML}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4" style="text-align: right;">Total</td>
        <td class="amount">${formatCurrency(totalAmount)}</td>
        <td colspan="3"></td>
      </tr>
    </tfoot>
  </table>` : ''}

  <!-- Technician Observations -->
  ${jobCard.technician_observations ? `
  <div class="observations">
    <h3>Technician Observations</h3>
    <p>${jobCard.technician_observations}</p>
  </div>` : ''}

  <!-- Additional Notes -->
  ${jobCard.notes ? `
  <div class="notes-section">
    <h3>Additional Notes</h3>
    <p>${jobCard.notes}</p>
  </div>` : ''}

  <!-- Footer with signature lines -->
  <div class="footer">
    <div class="signature-line">Customer Signature</div>
    <div class="signature-line">Technician Signature</div>
  </div>
</body>
</html>`;

  // Open new window and write the HTML
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to print the job card.');
    return;
  }

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.print();
}
